import { invokeApi } from '../bl_libs/invokeApi';
import {LOCAL_STORAGE_KEYS} from "../utils/constants";
import {use_e2ee} from "../utils/utils";

export const getLicensesBillingSummary = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/admin/org/licenses_billing/summary_by_insider',
        method : 'GET',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };

    return invokeApi(requestObj, use_e2ee());
};

export const adjustInsiderCredits = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/credits/adjust',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        postData: data
    };

    return invokeApi(requestObj, use_e2ee());
};

export const getInsiderCreditLedger = (dq_insider_id, page = 1, limit = 20, transaction_type = '') => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: `/admin/dq_insider/billing/credits/ledger/${dq_insider_id}`,
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: {
            page,
            limit
        }
    };
    if (transaction_type) {
        requestObj.queryParams.transaction_type = transaction_type;
    }

    return invokeApi(requestObj, use_e2ee());
};

export const getCreditsLedgerList = (params = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    
    let requestObj = {
        path: '/admin/dq_insider/billing/credits/ledger/all',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: params
    };

    return invokeApi(requestObj, use_e2ee());
};

// ── DQ Token (DQT) ───────────────────────────────────────────────────────────
// DQT is a separate unit from credits: the currency a partner holds, which it
// converts one way into credits. These endpoints are admin-only on the API
// (adminUserGuard). See my-dq-api:
// src/docs/DQ Token, Credits & Licences — Technical Architecture.md

// GET — { rate: { usd_per_dqt, effective_from, note, set_by } | null, history: [...] }
export const getDqtRateService = (limit = 20) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/dqt/rate',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: { limit }
    };

    return invokeApi(requestObj, use_e2ee());
};

// POST — record a new rate (append-only). data: { usd_per_dqt, note? }
export const setDqtRateService = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/dqt/rate',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        postData: data
    };

    return invokeApi(requestObj, use_e2ee());
};

// POST — grant DQT against a recorded offline payment.
// data: { dq_insider_id, amount_usd, payment_reference, description? }
export const grantDqtService = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/dqt/grant',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        postData: data
    };

    return invokeApi(requestObj, use_e2ee());
};

// GET — one partner's DQT balance and history: { dqt_balance, entries, total, page, pages }
export const getInsiderDqtLedgerService = (dq_insider_id, page = 1, limit = 10, transaction_type) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/dqt/ledger',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: { dq_insider_id, page, limit, ...(transaction_type ? { transaction_type } : {}) }
    };

    return invokeApi(requestObj, use_e2ee());
};

// GET — every DQT movement across all workspaces, paginated.
// params: { page, limit, transaction_type, dq_insider_id, start_date, end_date }
export const getDqtLedgerList = (params = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/dqt/ledger/all',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: params
    };

    return invokeApi(requestObj, use_e2ee());
};

// GET — one workspace's DQT orders (checkouts and grants, pending included).
export const getInsiderDqtOrdersService = (dq_insider_id, page = 1, limit = 10) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/dqt/orders',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: { dq_insider_id, page, limit }
    };

    return invokeApi(requestObj, use_e2ee());
};

// GET — what granting a plan's own price would give: the plan's one-time USD
// price, the rate in force and the DQ Tokens it buys.
export const getPlanGrantPreviewService = (plan_key) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/plan/preview',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: { plan_key }
    };

    return invokeApi(requestObj, use_e2ee());
};

// POST — put a workspace on a plan with no subscription, optionally granting
// the agreed DQT in the same step.
// data: { dq_insider_id, plan_key, grant?: { amount_usd, payment_reference, description } }
export const assignPlanService = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/plan/assign',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        postData: data
    };

    return invokeApi(requestObj, use_e2ee());
};

// ── Payment settings and bank transfers (adminUserGuard) ─────────────────────

// GET — { wallets_enabled, wire_transfer: { enabled, bank_name, ... }, wire_missing_fields, updated_by, updated_at }
export const getBillingSettingsService = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/settings',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
    };

    return invokeApi(requestObj, use_e2ee());
};

// POST — save them. data: { wallets_enabled?, wire_transfer?: { enabled?, bank_name?, ... } }
export const saveBillingSettingsService = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/settings',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        postData: data
    };

    return invokeApi(requestObj, use_e2ee());
};

// GET — bank transfers across all workspaces. params: { status?, dq_insider_id?, page, limit }
export const getWireOrdersService = (params = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/wire/list',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: params
    };

    return invokeApi(requestObj, use_e2ee());
};

// GET — one proof file: { file_name, mime_type, content_base64 }
export const getWireProofService = (order_id, index = 0) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/wire/proof',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: { order_id, index }
    };

    return invokeApi(requestObj, use_e2ee());
};

// POST — approve or reject. data: { order_id, action: 'approve' | 'reject', note? }
export const reviewWireOrderService = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/wire/review',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        postData: data
    };

    return invokeApi(requestObj, use_e2ee());
};

// GET — payments received in Wise and what each matched. params: { status?, page, limit }
export const getBankPaymentsService = (params = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/wire/payments',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        queryParams: params
    };

    return invokeApi(requestObj, use_e2ee());
};

// POST — read Wise now and match what arrived. data: { hours? }
export const syncBankPaymentsService = (data = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/wire/payments/sync',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        postData: data
    };

    return invokeApi(requestObj, use_e2ee());
};

// POST — decide a payment waiting for review. data: { transaction_id, action: 'link' | 'ignore', order_id?, note? }
export const resolveBankPaymentService = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/admin/dq_insider/billing/wire/payments/resolve',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        postData: data
    };

    return invokeApi(requestObj, use_e2ee());
};

// ─── Sign-up applications (the portal's /powered-by-dq/chatbot chatbot) ─────────────

// GET — the chatbot's option labels (public; used to show stored answers as the applicant saw them).
export const getSignupCatalogService = () => invokeApi({ path: '/dq_insider/public/pbdq/catalog', method: 'GET' });

// GET — one queue. params: { type, status?, priority?, search?, page, limit }
export const getSignupApplicationsService = (params = {}) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/admin/dq_insider/pbdq/applications',
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        queryParams: params,
    }, use_e2ee());
};

// GET — one uploaded document, base64. → { file_name, mime_type, content_base64 }
export const getSignupApplicationDocumentService = (id, index = 0) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/admin/dq_insider/pbdq/applications/document',
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        queryParams: { id, index },
    }, use_e2ee());
};

// POST — { id, action: approve | reject | close | fulfil | unreject, note? }
export const reviewSignupApplicationService = (data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path: '/admin/dq_insider/pbdq/applications/review',
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        postData: data,
    }, use_e2ee());
};
